import { Component, Inject } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BuyersService, Buyer, CommChannel } from 'src/app/core/buyers.service';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { FormErrorStateMatcher, FormService } from 'src/app/core/form.service';
import { UtilsService } from 'src/app/core/utils.service';
import { formatDate } from '@angular/common';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MomentDateAdapter } from '@angular/material-moment-adapter';

const MY_DATE_FORMAT = {
  parse: {
    dateInput: 'DD/MM/YYYY', // this is how your date will be parsed from Input
  },
  display: {
    dateInput: 'DD/MM/YYYY', // this is how your date will get displayed on the Input
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-buyers-edit-dialog',
  templateUrl: './buyers-edit-dialog.component.html',
  styleUrls: ['./buyers-edit-dialog.component.scss'],

  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMAT },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
  ],
})
export class BuyersEditDialogComponent {
  buyer?: Buyer;
  buyer_personal_info_form_group: FormGroup = this.form_service.buyer_personal_info_form_group;
  buyer_personal_id_form_group: FormGroup = this.form_service.buyer_personal_id_form_group;
  buyer_residency_form_group: FormGroup = this.form_service.buyer_residency_form_group;
  buyer_domicile_form_group: FormGroup = this.form_service.buyer_domicile_form_group;
  buyer_aml_form_group: FormGroup = this.form_service.buyer_aml_form_group;
  note_form_group: FormGroup = this.form_service.note_form_group;

  error_matcher: FormErrorStateMatcher = this.form_service.matcher;
  is_buyer_edit_running: boolean = false;

  communication_channel_options = Object.values(CommChannel);

  constructor(
    private form_service: FormService,
    private dialog_ref: MatDialogRef<BuyersEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private buyers_service: BuyersService,
    private error_service: ErrorService,
    private utils_service: UtilsService,
  ) {
    this.buyer = data.buyer;
  }

  ngOnInit(): void {
    /* Buyer residency */
    this.buyer_residency_form_group.get(this.form_service.country_label)?.setValue(this.buyer?.residency?.country);
    this.buyer_residency_form_group.get(this.form_service.city_label)?.setValue(this.buyer?.residency?.city);
    this.buyer_residency_form_group.get(this.form_service.province_label)?.setValue(this.buyer?.residency?.province);
    this.buyer_residency_form_group.get(this.form_service.address_label)?.setValue(this.buyer?.residency?.address);
    this.buyer_residency_form_group.get(this.form_service.zip_code_label)?.setValue(this.buyer?.residency?.zip_code);
    this.buyer_residency_form_group
      .get(this.form_service.house_number_label)
      ?.setValue(this.buyer?.residency?.house_number);

    /* Buyer domicile */
    this.buyer_domicile_form_group.get(this.form_service.country_label)?.setValue(this.buyer?.domicile?.country);
    this.buyer_domicile_form_group.get(this.form_service.city_label)?.setValue(this.buyer?.domicile?.city);
    this.buyer_domicile_form_group.get(this.form_service.province_label)?.setValue(this.buyer?.domicile?.province);
    this.buyer_domicile_form_group.get(this.form_service.address_label)?.setValue(this.buyer?.domicile?.address);
    this.buyer_domicile_form_group.get(this.form_service.zip_code_label)?.setValue(this.buyer?.domicile?.zip_code);
    this.buyer_domicile_form_group
      .get(this.form_service.house_number_label)
      ?.setValue(this.buyer?.domicile?.house_number);

    /* Buyer personal */
    this.buyer_personal_info_form_group.get(this.form_service.buyer_firstname_label)?.setValue(this.buyer?.firstname);
    this.buyer_personal_info_form_group.get(this.form_service.buyer_lastname_label)?.setValue(this.buyer?.lastname);
    this.buyer_personal_info_form_group.get(this.form_service.tax_code_label)?.setValue(this.buyer?.tax_code);

    const birthday = this.buyer?.birthday ? formatDate(new Date(this.buyer.birthday), 'yyyy-MM-dd', 'it-IT') : null;
    this.buyer_personal_info_form_group.get(this.form_service.birthday_label)?.setValue(birthday);

    this.buyer_personal_info_form_group.get(this.form_service.birthplace_label)?.setValue(this.buyer?.birthplace);
    this.buyer_personal_info_form_group.get(this.form_service.birthregion_label)?.setValue(this.buyer?.birthregion);
    this.buyer_personal_info_form_group.get(this.form_service.birthcountry_label)?.setValue(this.buyer?.birthcountry);
    this.buyer_personal_info_form_group
      .get(this.form_service.birthregion_code_label)
      ?.setValue(this.buyer?.birthregion_code);
    this.buyer_personal_info_form_group.get(this.form_service.citizenship_label)?.setValue(this.buyer?.citizenship);
    this.buyer_personal_info_form_group
      .get(this.form_service.secondary_citizenship_label)
      ?.setValue(this.buyer?.secondary_citizenship);
    this.buyer_personal_info_form_group.get(this.form_service.gender_label)?.setValue(this.buyer?.gender);
    this.buyer_personal_info_form_group.get(this.form_service.cdg_label)?.setValue(this.buyer?.cdg);
    this.buyer_personal_info_form_group.get(this.form_service.email_label)?.setValue(this.buyer?.email);
    this.buyer_personal_info_form_group
      .get(this.form_service.communication_channel_label)
      ?.setValue(this.buyer?.communication_channel);

    /* Buyer personal Id (document)  */
    this.buyer_personal_id_form_group.get(this.form_service.issuer_label)?.setValue(this.buyer?.document?.issuer);
    this.buyer_personal_id_form_group.get(this.form_service.doc_type_label)?.setValue(this.buyer?.document?.doc_type);
    this.buyer_personal_id_form_group
      .get(this.form_service.doc_number_label)
      ?.setValue(this.buyer?.document?.doc_number);
    this.buyer_personal_id_form_group
      .get(this.form_service.issuer_type_label)
      ?.setValue(this.buyer?.document?.issuer_type);
    this.buyer_personal_id_form_group
      .get(this.form_service.issue_province_label)
      ?.setValue(this.buyer?.document?.issue_province);
    this.buyer_personal_id_form_group
      .get(this.form_service.issue_country_label)
      ?.setValue(this.buyer?.document?.issue_country);
    this.buyer_personal_id_form_group
      .get(this.form_service.emission_date_label)
      ?.setValue(this.buyer?.document?.emission_date);
    this.buyer_personal_id_form_group
      .get(this.form_service.expiration_date_label)
      ?.setValue(this.buyer?.document?.expiration_date);

    /* Buyer AML  */
    this.buyer_aml_form_group.get(this.form_service.activity_label)?.setValue(this.buyer?.aml?.activity);
    this.buyer_aml_form_group.get(this.form_service.sector_label)?.setValue(this.buyer?.aml?.sector);
    this.buyer_aml_form_group.get(this.form_service.occupation_label)?.setValue(this.buyer?.aml?.occupation);

    const is_apical_subject = this.buyer?.aml?.is_apical_subject ? 'yes' : 'no';
    this.buyer_aml_form_group.get(this.form_service.is_apical_subject_label)?.setValue(is_apical_subject);
    this.buyer_aml_form_group.get(this.form_service.business_place_label)?.setValue(this.buyer?.aml?.business_place);
    this.buyer_aml_form_group.get(this.form_service.tae_label)?.setValue(this.buyer?.aml?.tae);
    this.buyer_aml_form_group.get(this.form_service.activity_code_label)?.setValue(this.buyer?.aml?.activity_code);
    this.buyer_aml_form_group.get(this.form_service.sector_code_label)?.setValue(this.buyer?.aml?.sector_code);
    this.buyer_aml_form_group.get(this.form_service.occupation_code_label)?.setValue(this.buyer?.aml?.occupation_code);
  }

  onCloseClick(): void {
    this.note_form_group.reset();
    this.dialog_ref.close();
  }

  onConfirmClick(): void {
    if (
      this.isResidencyFormValid() &&
      this.isPersonalInfoFormValid() &&
      this.isPersonalIdFormValid() &&
      this.isNoteFormValid() &&
      this.buyer?.buyer_id
    ) {
      const admin_email = this.utils_service.getProfileEmail();

      const buyer: Buyer = {
        ...this.buyer,
        residency: {
          ...this.buyer.residency,
          ...this.buyer_residency_form_group.value,
        },
        domicile: {
          ...this.buyer.domicile,
          ...this.buyer_domicile_form_group.value,
        },
        firstname: this.buyer_personal_info_form_group.get(this.form_service.buyer_firstname_label)?.value,
        lastname: this.buyer_personal_info_form_group.get(this.form_service.buyer_lastname_label)?.value,
        tax_code: this.buyer_personal_info_form_group.get(this.form_service.tax_code_label)?.value,
        birthday: new Date(this.buyer_personal_info_form_group.get(this.form_service.birthday_label)?.value).getTime(),
        birthplace: this.buyer_personal_info_form_group.get(this.form_service.birthplace_label)?.value,
        birthregion: this.buyer_personal_info_form_group.get(this.form_service.birthregion_label)?.value,
        birthregion_code: this.buyer_personal_info_form_group.get(this.form_service.birthregion_code_label)?.value,
        birthcountry: this.buyer_personal_info_form_group.get(this.form_service.birthcountry_label)?.value,
        citizenship: this.buyer_personal_info_form_group.get(this.form_service.citizenship_label)?.value,
        secondary_citizenship: this.buyer_personal_info_form_group.get(this.form_service.secondary_citizenship_label)
          ?.value,
        gender: this.buyer_personal_info_form_group.get(this.form_service.gender_label)?.value,
        cdg: this.buyer_personal_info_form_group.get(this.form_service.cdg_label)?.value,
        email: this.buyer_personal_info_form_group.get(this.form_service.email_label)?.value,
        communication_channel: this.buyer_personal_info_form_group.get(this.form_service.communication_channel_label)
          ?.value,
        document: {
          issuer: this.buyer_personal_id_form_group.get(this.form_service.issuer_label)?.value,
          doc_type: this.buyer_personal_id_form_group.get(this.form_service.doc_type_label)?.value,
          doc_number: this.buyer_personal_id_form_group.get(this.form_service.doc_number_label)?.value,
          issuer_type: this.buyer_personal_id_form_group.get(this.form_service.issuer_type_label)?.value,
          issue_province: this.buyer_personal_id_form_group.get(this.form_service.issue_province_label)?.value,
          issue_country: this.buyer_personal_id_form_group.get(this.form_service.issue_country_label)?.value,

          /* yyyy-mm-dd format to preserve coherence whit Experian and Qinetic*/
          emission_date: formatDate(
            new Date(this.buyer_personal_id_form_group.get(this.form_service.emission_date_label)?.value),
            'yyyy-MM-dd',
            'it-IT',
          ),
          expiration_date: formatDate(
            new Date(this.buyer_personal_id_form_group.get(this.form_service.expiration_date_label)?.value),
            'yyyy-MM-dd',
            'it-IT',
          ),
        },
        aml: {
          ...this.buyer.aml,
          activity: this.buyer_aml_form_group.get(this.form_service.activity_label)?.value,
          sector: this.buyer_aml_form_group.get(this.form_service.sector_label)?.value,
          occupation: this.buyer_aml_form_group.get(this.form_service.occupation_label)?.value,
          is_apical_subject: this.buyer_aml_form_group.get(this.form_service.is_apical_subject_label)?.value === 'yes',
          business_place: this.buyer_aml_form_group.get(this.form_service.business_place_label)?.value,
          tae: this.buyer_aml_form_group.get(this.form_service.tae_label)?.value,
          activity_code: this.buyer_aml_form_group.get(this.form_service.activity_code_label)?.value,
          sector_code: this.buyer_aml_form_group.get(this.form_service.sector_code_label)?.value,
          occupation_code: this.buyer_aml_form_group.get(this.form_service.occupation_code_label)?.value,
        },
        latest_edit: {
          admin_email: admin_email,
          note: this.note_form_group.get(this.form_service.note_label)?.value,
          timestamp: new Date().getTime(),
        },
      };

      this.edit(buyer);
    }
  }

  edit(buyer: Buyer): void {
    this.is_buyer_edit_running = true;
    this.buyers_service.edit(buyer).subscribe({
      next: () => {
        this.note_form_group.reset();
        this.dialog_ref.close({ success: true });
        this.is_buyer_edit_running = false;
      },
      error: error => {
        const processed_error: SplittypayError = this.error_service.preProcessApiError(error);
        this.dialog_ref.close({ error: processed_error });
        this.is_buyer_edit_running = false;
      },
    });
  }

  isPersonalInfoFormValid(): boolean {
    return this.form_service.validateForm(this.buyer_personal_info_form_group);
  }

  isPersonalIdFormValid(): boolean {
    return this.form_service.validateForm(this.buyer_personal_id_form_group);
  }

  isResidencyFormValid(): boolean {
    return this.form_service.validateForm(this.buyer_residency_form_group);
  }

  isDomicileFormValid(): boolean {
    return this.form_service.validateForm(this.buyer_domicile_form_group);
  }

  isAmlFormValid(): boolean {
    return this.form_service.validateForm(this.buyer_aml_form_group);
  }

  isNoteFormValid(): boolean {
    return this.form_service.validateForm(this.note_form_group);
  }
}
